import { useMemo, type FC } from 'react'

import { InfoBox, Skeleton } from '@skytech/ui'
import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { twJoin } from 'tailwind-merge'

import queries from '@/api/queries'
import { recalculatePriceWithCover } from '@/components/ecom-policy/policy/helpers'
import type {
  EcomPolicyMandatoryProduct,
  GetMandatoryProductsResponse,
  Product,
} from '@/components/ecom-policy/types'
import { useCart } from '@/contexts/cart/cart-context'
import { useIsThreeStepIT } from '@/hooks/user'
import { ecomPolicyResources } from '@/resources/ecom-policy'

import { Translation } from '../i18n/Translation'

import { CartGreenpoolProduct, LeaseMessage } from './CartGreenpoolProduct'
import { CartProduct } from './CartProduct'

export const CartPolicyList: FC<{
  mandatoryProductsQueries: UseQueryResult<GetMandatoryProductsResponse>[]
  invalidProductPrices?: Record<number, { oldPrice: number; newPrice: number }>
  onRemoveItem: (policyId: number, productId: number, force?: boolean) => void
}> = ({ mandatoryProductsQueries, invalidProductPrices, onRemoveItem }) => {
  const isThreeStepIT = useIsThreeStepIT()

  const { getItemsGroupedByPolicy } = useCart()
  const { data: policiesData, isLoading: isLoadingPolicies } = useQuery(
    queries.policyList(),
    ecomPolicyResources.getList
  )

  const isLoadingMandatoryProducts = mandatoryProductsQueries.some(q => q.isLoading)

  const combinedData = useMemo(
    () =>
      Object.entries(getItemsGroupedByPolicy()).map(([policyId, items]) => {
        const mandatoryProducts =
          mandatoryProductsQueries.find(q => q.data && q.data.policyId === Number(policyId))?.data
            ?.mandatoryProducts || []
        const mappedMandatory = mandatoryProducts.map(p => ({ ...p, isMandatory: 1 }))

        const isGreenpool = items.some(item => item.isGreenpoolProduct)
        if (isGreenpool) {
          const greenpoolAssets =
            items
              .filter(
                item => Boolean(item.asset) && item.isGreenpoolProduct && !item.product?.isMandatory
              )
              .map(item => {
                if (!item.asset) return null
                return {
                  ...item.asset,
                  id: Number(item.productId),
                }
              }) || []
          return {
            policyId: Number(policyId),
            products: [],
            greenpoolAssets,
          }
        }

        const selectedProducts =
          items
            .filter(i => Boolean(i.product) && !i.isGreenpoolProduct && !i.product?.isMandatory)
            .map(i => ({
              ...i.product,
              qty: i.qty,
              id: Number(i.productId),
            })) || []
        const allProducts = [...selectedProducts, ...mappedMandatory]
        return { policyId: Number(policyId), products: allProducts, greenpoolAssets: [] }
      }),
    [getItemsGroupedByPolicy, mandatoryProductsQueries]
  )

  if (isLoadingPolicies || isLoadingMandatoryProducts) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col pt-2 sm:mb-4 sm:gap-2 sm:rounded-lg sm:border sm:border-solid sm:border-gray-300 sm:p-2 md:p-3">
          <Skeleton height={100} />
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      {combinedData.map(policyData => {
        const policy = policiesData?.policyList.find(p => p.id === Number(policyData.policyId))
        if (!policy) return null
        const mandatoryProducts = policyData.products.filter(p => p.isMandatory === 1)

        const priceList = policyData.products
          .filter(p => !p.isMandatory)
          .map(product => ({ ...product })) as Product[]
        const { allocation } = recalculatePriceWithCover({
          coverAvailable: policy.coverAmount - policy.coverUsed,
          priceList: priceList,
          selectedProductIds: priceList.map(item => item.id),
        })
        const isGreenpool = policyData.greenpoolAssets.length > 0

        // To get correct new prices
        const newPriceList =
          invalidProductPrices && !isGreenpool
            ? (policyData.products as Product[])
                .filter(p => !p.isMandatory)
                .map(p => ({
                  ...p,
                  price: invalidProductPrices?.[p.id]?.newPrice || p.price,
                }))
            : []
        const { allocation: allocationNew = {} } =
          invalidProductPrices && !isGreenpool
            ? recalculatePriceWithCover({
                coverAvailable: policy.coverAmount - policy.coverUsed,
                priceList: newPriceList,
                selectedProductIds: newPriceList.map(item => item.id),
              })
            : {}

        return (
          <div
            key={policyData.policyId}
            className="flex flex-col pt-2 sm:mb-4 sm:gap-2 sm:rounded-lg sm:border sm:border-solid sm:border-gray-300 sm:p-2 md:p-3"
          >
            <div className="flex items-center gap-2">
              <h2 className="sm:!text-md !m-0 !text-left !text-sm font-semibold md:!text-lg">
                {policy.name}
              </h2>
              {isGreenpool && (
                <RecycleIcon
                  size={20}
                  className={twJoin(
                    isThreeStepIT
                      ? 'fill-threeStepIT text-threeStepIT'
                      : 'fill-green-badge text-green-badge'
                  )}
                  color={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-badge')}
                />
              )}
            </div>

            {/* Greenpool assets */}
            {isGreenpool && policyData.greenpoolAssets?.length > 0 && (
              <div className="-mx-2 flex flex-col sm:mx-0 sm:gap-2">
                {policyData.greenpoolAssets.map(asset => {
                  if (!asset) return null
                  return (
                    <div key={asset.id} className="flex flex-col gap-4">
                      <LeaseMessage selectedGreenpoolProduct={asset} />
                      <CartGreenpoolProduct
                        asset={asset}
                        policy={policy}
                        onRemoveItem={onRemoveItem}
                      />
                    </div>
                  )
                })}
              </div>
            )}

            {/* Adjust margin to ensure full width alignment with the container having id="base-container" */}
            {!isGreenpool && (
              <div className="-mx-2 flex flex-col sm:mx-0 sm:gap-2">
                {policyData.products.map(product => {
                  if (!product) return null
                  const isMandatory = product.isMandatory === 1
                  if (isMandatory) return null
                  const changedProductPrice = invalidProductPrices?.[product.id]

                  const userNewPrice =
                    changedProductPrice?.newPrice && allocationNew?.[product.id]
                      ? allocationNew[product.id]?.userPrice
                      : undefined

                  return (
                    <CartProduct
                      key={product.id}
                      policy={policy}
                      product={product as (Product | EcomPolicyMandatoryProduct) & { qty: number }}
                      price={allocation[product.id]?.userPrice}
                      newProductPrice={userNewPrice}
                      onRemoveItem={onRemoveItem}
                    />
                  )
                })}
                {mandatoryProducts.length > 0 && (
                  <InfoBox
                    emphasis="warning"
                    className="m-0 !mx-2 mt-4 text-sm sm:!mx-0 sm:text-base"
                  >
                    <AlertIcon />
                    <Translation id="Following items automatically added to your order. Employer will fully cover their cost." />
                  </InfoBox>
                )}
                {mandatoryProducts.map(product => {
                  if (!product) return null
                  return (
                    <CartProduct
                      key={product.id}
                      policy={policy}
                      product={product as (Product | EcomPolicyMandatoryProduct) & { qty: number }}
                      onRemoveItem={onRemoveItem}
                    />
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
